import React, { useState, useEffect } from 'react';
import { ArrowDownIcon, LinkIcon } from '@heroicons/react/24/outline';
import { preferencesApi } from '../../../repositories/preferences';
import { useAppSelector } from '../../../hooks/redux';
import TermsOfServiceModal from '../../modal/TermsOfServiceModal';
import { userApi } from '../../../repositories/user';
import PrivacyPolicyModal from '../../modal/PrivacyPolicyModal';

interface Preferences {
  dataSharing: boolean;
  usageAnalytics: boolean;
  emailNotifications: boolean;
}

const Privacy: React.FC = () => {
  const [dataSharing, setDataSharing] = useState<boolean>(true);
  const [usageAnalytics, setUsageAnalytics] = useState<boolean>(true);
  const [emailNotifications, setEmailNotifications] = useState<boolean>(true);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const { user } = useAppSelector((state) => state.auth);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);


  useEffect(() => {
    if (!user) return;
    const fetchPreferences = async () => {
      try {
        const { data } = await preferencesApi.getPreferences(user.id);
        setDataSharing(data.dataSharing);
        setUsageAnalytics(data.usageAnalytics);
        setEmailNotifications(data.emailNotifications);
      } catch (error: any) {
        console.error('Error fetching preferences:', error.response?.data?.error || error.message);
      }
    };
    fetchPreferences();
  }, [user]);

  const updatePreference = async (updates: Partial<Preferences>) => {
    if (!user) return;
    try {
      await preferencesApi.updatePreferences(user.id, updates);
    } catch (error: any) {
      console.error('Error updating preferences:', error.response?.data?.error || error.message);
    }
  };

  const handleToggle = (
    setter: React.Dispatch<React.SetStateAction<boolean>>,
    currentValue: boolean,
    field: keyof Preferences
  ) => {
    const newValue = !currentValue;
    setter(newValue);
    updatePreference({ [field]: newValue });
  };

  const handleExportData = async () => {
    if (!user) return;
    setExportLoading(true);
    setExportSuccess(false);
    setExportError(null);

    try {
      if (typeof userApi.exportData !== "function") {
        setExportError("Export function not available.");
        setExportLoading(false);
        return;
      }

      const response = await userApi.exportData();

      if (!response.data) {
        setExportError("No pitch data found to export.");
        setExportLoading(false);
        return;
      }

      // Get filename from header or fallback
      const disposition = response.headers["content-disposition"];
      let filename = `cadence-pitch-data-${new Date().toISOString().slice(0, 10)}.csv`;
      if (disposition && disposition.includes("filename=")) {
        filename = disposition.split("filename=")[1].replace(/['"]/g, "").trim();
      }

      // Create blob from CSV string content
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);

      // Download file
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setExportSuccess(true);
    } catch (error: any) {
      console.error("Export error:", error);
      if (error.response?.status === 404) {
        setExportError("No pitch data found to export.");
      } else if (error.response?.data?.error) {
        setExportError(error.response.data.error);
      } else {
        setExportError("Network error. Please try again.");
      }
    } finally {
      setExportLoading(false);
      setTimeout(() => setExportSuccess(false), 3000); // Hide success after 3s
    }
  };

  return (
    <>
      <div className="max-w-3xl p-4 bg-white rounded-lg shadow-sm sm:p-6">
        <h2 className="mb-4 text-base font-semibold text-gray-800 sm:text-lg">Privacy & Data Settings</h2>
        <hr className="mb-4 border-gray-200 sm:mb-6" />

        {/* Data Preferences */}
        <div className="mb-4 sm:mb-6">
          <h3 className="mb-4 text-sm font-semibold text-gray-800 sm:text-base">Data Preferences</h3>
          <div className="space-y-4">
            {/* Data Sharing Toggle */}
            <div className="p-3 border border-gray-200 rounded-md sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-800 sm:text-sm">Data sharing with connected DSPs</p>
                  <p className="text-xs text-gray-600">Allow Cadence to share pitch data with streaming platforms</p>
                </div>
                <button
                  onClick={() => handleToggle(setDataSharing, dataSharing, 'dataSharing')}
                  className={`w-8 sm:w-10 h-5 sm:h-6 rounded-full p-1 flex items-center ${dataSharing ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                >
                  <div
                    className={`w-3 sm:w-4 h-3 sm:h-4 rounded-full bg-white transform transition-transform ${dataSharing ? 'translate-x-3 sm:translate-x-4' : 'translate-x-0'
                      }`}
                  />
                </button>
              </div>
            </div>

            {/* Usage Analytics Toggle */}
            <div className="p-3 border border-gray-200 rounded-md sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-800 sm:text-sm">Usage analytics opt-in</p>
                  <p className="text-xs text-gray-600">Help us improve by sharing anonymous usage data</p>
                </div>
                <button
                  onClick={() => handleToggle(setUsageAnalytics, usageAnalytics, 'usageAnalytics')}
                  className={`w-8 sm:w-10 h-5 sm:h-6 rounded-full p-1 flex items-center ${usageAnalytics ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                >
                  <div
                    className={`w-3 sm:w-4 h-3 sm:h-4 rounded-full bg-white transform transition-transform ${usageAnalytics ? 'translate-x-3 sm:translate-x-4' : 'translate-x-0'
                      }`}
                  />
                </button>
              </div>
            </div>

            {/* Email Notifications Toggle */}
            <div className="p-3 border border-gray-200 rounded-md sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-800 sm:text-sm">Email notifications</p>
                  <p className="text-xs text-gray-600">Receive updates about your pitches and account</p>
                </div>
                <button
                  onClick={() => handleToggle(setEmailNotifications, emailNotifications, 'emailNotifications')}
                  className={`w-8 sm:w-10 h-5 sm:h-6 rounded-full p-1 flex items-center ${emailNotifications ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                >
                  <div
                    className={`w-3 sm:w-4 h-3 sm:h-4 rounded-full bg-white transform transition-transform ${emailNotifications ? 'translate-x-3 sm:translate-x-4' : 'translate-x-0'
                      }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Data & Legal */}
        <div className="mb-4 sm:mb-6">
          <h3 className="mb-4 text-sm font-semibold text-gray-800 sm:text-base">Data & Legal</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Export Data */}
            <button
              onClick={handleExportData}
              className="relative flex items-center p-3 transition-colors border border-gray-200 rounded-md sm:p-4 hover:bg-gray-50"
              disabled={exportLoading}
            >
              <ArrowDownIcon className="w-4 h-4 mr-2 text-gray-600 sm:w-5 sm:h-5" />
              <span className="text-xs text-gray-800 sm:text-sm">
                {exportLoading ? "Exporting..." : "Export Your Data"}
              </span>
              {exportLoading && (
                <span className="w-4 h-4 ml-2 border-2 border-blue-400 rounded-full border-t-transparent animate-spin"></span>
              )}
              {exportSuccess && (
                <span className="ml-2 text-xs font-semibold text-green-600">Downloaded!</span>
              )}
              {exportError && (
                <span className="ml-2 text-xs font-semibold text-red-600">{exportError}</span>
              )}
            </button>

            {/* Privacy Policy */}
            {/* <a href="/privacy-policy" target="_blank" rel="noopener noreferrer"
              className="flex items-center p-3 transition-colors border border-gray-200 rounded-md sm:p-4 hover:bg-gray-50"
            >
              <LinkIcon className="w-4 h-4 mr-2 text-gray-600 sm:w-5 sm:h-5" />
              <span className="text-xs text-gray-800 sm:text-sm">Privacy Policy</span>
            </a> */}
            <button
              onClick={() => setShowPrivacyModal(true)}
              className="relative flex items-center p-3 transition-colors border border-gray-200 rounded-md sm:p-4 hover:bg-gray-50"
            >
              <LinkIcon className="w-4 h-4 mr-2 text-gray-600 sm:w-5 sm:h-5" />
              <span className="text-xs text-gray-800 sm:text-sm">Privacy Policy</span>
            </button>


            {/* Terms of Service */}
            <button
              onClick={() => setShowTermsModal(true)}
              className="flex items-center p-3 transition-colors border border-gray-200 rounded-md sm:p-4 hover:bg-gray-50 sm:col-span-2"
            >
              <LinkIcon className="w-4 h-4 mr-2 text-gray-600 sm:w-5 sm:h-5" />
              <span className="text-xs text-gray-800 sm:text-sm">Terms of Service</span>
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      <TermsOfServiceModal
        isOpen={showTermsModal}
        onAccept={() => setShowTermsModal(false)}
        onDecline={() => setShowTermsModal(false)}
      />



      <PrivacyPolicyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />

    </>
  );
};

export default Privacy;